import { useState, useEffect, useCallback } from "react";

const PREFIX = "ui_state:";

/**
 * sessionStorage 기반으로 화면 상태를 유지하는 훅.
 *
 * 브라우저가 백그라운드 탭을 메모리 회수 목적으로 리로드(discard)하더라도
 * 검색어·필터·페이지 번호·선택 탭 등 UI 상태가 초기화되지 않도록 복원한다.
 * 탭(세션)이 닫히면 자동으로 정리되고, 같은 탭에서는 새로고침/복귀에도 유지된다.
 */
export default function usePersistentState<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const storageKey = PREFIX + key;

  const readValue = (): T => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw === null) return initialValue;
      return JSON.parse(raw) as T;
    } catch {
      return initialValue;
    }
  };

  const [value, setValue] = useState<T>(readValue);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // 저장 실패(용량 초과 등)는 무시
    }
  }, [storageKey, value]);

  const update = useCallback((next: T | ((prev: T) => T)) => {
    setValue(next);
  }, []);

  return [value, update];
}